import type { Example } from '../types';

export const introCameraPathExample: Example = {
  id: 'intro-camera-path',
  title: 'Intro Camera Path',
  categoryId: 'viewer',
  description: 'camera_path() fly-in on load; scene_camera() initial view',
  scadCode: `/* Intro Camera Path */

// markdown:tip
// **Intro Camera Path** — \`camera_path()\` waypoints play automatically
// when the model first loads, animating the camera through each position
// before settling at the last one. A **Replay** button appears in the
// viewer toolbar. \`scene_camera()\` sets the final resting position.

/* [Camera] */

// Final FOV
final_fov = 35; // [15:1:75]

// Replay on parameter change?
// (Enable to re-trigger the fly-in when you adjust sliders)

/* [Model] */

// Tower count
towers = 5; // [1:1:8]

// Tower height
tower_h = 50; // [20:1:90]

// Base radius
base_r = 12; // [6:0.5:22]

// Color
// color
tower_color = [0.35, 0.52, 0.75];

// --- parameters end ---

$fn = 40;
spread = 35;

// ── Scene camera — where the camera rests after the path ─────────────────
scene_camera(
  position = [0, -140, 80],
  target   = [0, 0, tower_h / 2],
  fov      = final_fov
);

// ── Intro camera path — plays once on load ────────────────────────────────

// Waypoint 1 — wide aerial establishing shot
camera_path(
  position   = [0, 0, 220],
  target     = [0, 0, 0],
  fov        = 50,
  transitions = "zoom_out",
  zoom_out_factor = 1.5,
  easing     = "ease_in_out",
  duration   = 1.8
);

// Waypoint 2 — arc swing to front-quarter view
camera_path(
  position   = [120, -100, 100],
  target     = [0, 0, tower_h / 2],
  fov        = 42,
  transitions = "arc,roll",
  arc_height  = 60,
  roll_angle  = -15,
  easing      = "ease_in_out",
  duration    = 2.2
);

// Waypoint 3 — settle into final position
camera_path(
  position   = [0, -140, 80],
  target     = [0, 0, tower_h / 2],
  fov        = final_fov,
  transitions = "orbit,zoom_out",
  orbit_turns = 0.25,
  zoom_out_factor = 1.3,
  easing     = "ease_in_out",
  duration   = 2.0
);

// ── Geometry ──────────────────────────────────────────────────────────────

for (i = [0:towers-1]) {
  angle  = i * (360 / towers);
  dist   = (towers == 1) ? 0 : spread;
  h_mult = 0.5 + 0.5 * ((i % 3) / 2.0);

  color(tower_color)
  rotate([0, 0, angle])
  translate([dist, 0, 0]) {
    // Base
    cylinder(d=base_r * 2, h=6, center=false);
    // Tower shaft
    translate([0, 0, 6])
      cylinder(d=base_r * 1.1, h=tower_h * h_mult, center=false);
    // Cap
    translate([0, 0, 6 + tower_h * h_mult])
      cylinder(d1=base_r * 1.4, d2=0, h=base_r * 1.2, center=false);
  }
}

// Ground disc
color([0.18, 0.18, 0.2])
material(material_def(roughness=0.9))
translate([0, 0, -1])
  cylinder(d=(spread + base_r) * 2 + 20, h=2, center=false);

// Lighting
light_directional(color=[1.0, 0.95, 0.85], position=[80, -60, 130], intensity=1.5);
light_directional(color=[0.4, 0.55, 0.9],  position=[-80, 40, 60],  intensity=0.4);
light_ambient(color=[1,1,1], intensity=0.3);
`,
};
